import { PayloadAction } from '@reduxjs/toolkit';
import type { Action } from 'redux';
import { call, put, race, select, take, takeEvery } from 'redux-saga/effects';

import { showNotification } from 'src/utils';
import { fetchPromptStatus, fetchPromptTemplates, submitPrompt } from './api';
import {
  AI_POLL_INTERVAL_MS,
  AI_POLL_MAX_ATTEMPTS,
  AI_TEMPLATES_TOAST_ID,
} from './constants';
import {
  isFailedStatus,
  isPendingStatus,
  pickTemplate,
  promptVariables,
  readGeneratedText,
  textToParagraphs,
} from './contract';
import { rephraseText } from './mockApi';
import {
  clearPreview,
  generateDraftFailure,
  generateDraftRequest,
  generateDraftSuccess,
  rejectPreview,
  rephraseFailure,
  rephraseRequest,
  rephraseSuccess,
} from './slice';
import {
  fetchTemplatesFailure,
  fetchTemplatesRequest,
  fetchTemplatesSuccess,
  selectPromptTemplates,
} from './templatesSlice';
import {
  AiNarrativeIdPayload,
  FetchTemplatesRequestPayload,
  GenerateDraftRequestPayload,
  RephraseRequestPayload,
} from './types';
import { fillTemplate, promptTemplateKey, promptTemplateParams } from './utils';

export function* FetchPromptTemplatesSaga(
  action: PayloadAction<FetchTemplatesRequestPayload>,
) {
  const { key, params } = action.payload;
  try {
    const items = yield call(fetchPromptTemplates, params);
    yield put(fetchTemplatesSuccess({ key, items }));
  } catch (e) {
    yield put(
      fetchTemplatesFailure({
        key,
        error: e.message || 'Prompt templates failed',
      }),
    );
    showNotification('Unable to load the AI prompt templates', 'error', {
      toastId: AI_TEMPLATES_TOAST_ID,
    });
    console.error(e);
  }
}

export const delay = (ms: number) =>
  new Promise(resolve => setTimeout(resolve, ms));

// Done when the text appears, so the literal name of the success state does not
// matter; the timeout is what releases the editor if the job never finishes.
export function* PollPromptStatusSaga(eventId: number | string) {
  for (let attempt = 0; attempt < AI_POLL_MAX_ATTEMPTS; attempt++) {
    yield delay(AI_POLL_INTERVAL_MS);

    const payload = yield call(fetchPromptStatus, eventId);

    if (isFailedStatus(payload?.status)) {
      throw new Error(`Prompt status: the job failed (${payload.status})`);
    }

    const text = readGeneratedText(payload);
    if (text && !isPendingStatus(payload?.status)) {
      return text;
    }
  }

  throw new Error(
    `Prompt status: still pending after ${Math.round(
      (AI_POLL_MAX_ATTEMPTS * AI_POLL_INTERVAL_MS) / 1000,
    )}s, giving up`,
  );
}

export function* RunDraftGenerationSaga(
  action: PayloadAction<GenerateDraftRequestPayload>,
) {
  const { narrativeId, currentContent, context } = action.payload;

  const templates = yield select(
    selectPromptTemplates(promptTemplateKey(promptTemplateParams(context))),
  );
  const template = pickTemplate(templates.items, 'generate_draft');
  const prompt = fillTemplate(
    template,
    promptVariables(context, { currentContent }),
  );

  const submission = yield call(submitPrompt, prompt);
  if (!submission?.event_id) {
    throw new Error(
      `Prompt submission: no event_id in the response (fields: ${
        Object.keys(submission || {}).join(', ') || 'none'
      })`,
    );
  }

  const generated = yield call(PollPromptStatusSaga, submission.event_id);

  yield put(
    generateDraftSuccess({
      narrativeId,
      draftHtml: textToParagraphs(generated),
      references: [],
    }),
  );
}

export function* GenerateDraftSaga(
  action: PayloadAction<GenerateDraftRequestPayload>,
) {
  const { narrativeId } = action.payload;

  const abandonedThisNarrative = (candidate: Action): boolean => {
    const { type, payload } = candidate as PayloadAction<AiNarrativeIdPayload>;
    return (
      (type === clearPreview.type || type === rejectPreview.type) &&
      payload?.narrativeId === narrativeId
    );
  };

  try {
    // Leaving edit mode abandons the job instead of letting a late answer land.
    yield race({
      done: call(RunDraftGenerationSaga, action),
      cancelled: take(abandonedThisNarrative),
    });
  } catch (e) {
    yield put(
      generateDraftFailure({
        narrativeId,
        error: e.message || 'Draft generation failed',
      }),
    );
    showNotification('Unable to generate the AI draft', 'error');
    console.error(e);
  }
}

export function* RephraseSaga(action: PayloadAction<RephraseRequestPayload>) {
  const { narrativeId, selectedText, action: rephraseAction } = action.payload;
  try {
    const result = yield call(rephraseText, selectedText, rephraseAction);
    yield put(
      rephraseSuccess({ narrativeId, proposedText: result.proposedText }),
    );
  } catch (e) {
    yield put(
      rephraseFailure({
        narrativeId,
        error: e.message || 'Rephrase failed',
      }),
    );
    showNotification('Unable to rephrase the selected text', 'error');
    console.error(e);
  }
}

export default function* aiNarrativeSaga() {
  yield takeEvery(generateDraftRequest.type, GenerateDraftSaga);
  yield takeEvery(rephraseRequest.type, RephraseSaga);
  yield takeEvery(fetchTemplatesRequest.type, FetchPromptTemplatesSaga);
}
